/**
 * Connection Status Alert Banner
 * 
 * Shows alerts when a bank connection is inactive, expired, or has errors.
 * Prompts user to reconnect.
 */

import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import {
  AlertTriangle,
  RefreshCw,
  XCircle,
  Clock,
  ArrowRight
} from 'lucide-react';

const STATUS_CONFIG = {
  inactive: {
    icon: Clock,
    title: 'Bank connection inactive',
    description: 'Your bank connection needs to be refreshed to continue syncing.',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    borderColor: 'border-amber-200 dark:border-amber-800',
    iconColor: 'text-amber-600 dark:text-amber-400',
    textColor: 'text-amber-800 dark:text-amber-200'
  },
  expired: {
    icon: Clock,
    title: 'Bank connection expired',
    description: 'Your authorization has expired. Please reconnect to continue syncing transactions.',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    borderColor: 'border-amber-200 dark:border-amber-800',
    iconColor: 'text-amber-600 dark:text-amber-400',
    textColor: 'text-amber-800 dark:text-amber-200'
  },
  error: {
    icon: XCircle,
    title: 'Bank connection error',
    description: 'We couldn\'t sync your bank data. This may be temporary, or you may need to reconnect.',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-600 dark:text-red-400',
    textColor: 'text-red-800 dark:text-red-200'
  },
  revoked: {
    icon: XCircle,
    title: 'Bank access revoked',
    description: 'Access to your bank was revoked externally. Please reconnect to continue.',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-600 dark:text-red-400',
    textColor: 'text-red-800 dark:text-red-200'
  },
  disconnected: {
    icon: AlertTriangle,
    title: 'Bank disconnected',
    description: 'This bank connection has been disconnected. Reconnect to resume syncing.',
    bgColor: 'bg-slate-50 dark:bg-slate-800/50',
    borderColor: 'border-slate-200 dark:border-slate-700',
    iconColor: 'text-slate-600 dark:text-slate-400',
    textColor: 'text-slate-700 dark:text-slate-300'
  }
};

export default function ConnectionStatusAlert({ 
  status, 
  connectionName,
  onReconnect,
  showReconnect = true,
  compact = false
}) {
  const config = STATUS_CONFIG[status];
  
  if (!config) return null;
  
  const Icon = config.icon;
  
  if (compact) {
    return (
      <div 
        className={`p-3 rounded-lg border ${config.bgColor} ${config.borderColor}`}
        data-testid={`connection-alert-${status}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${config.iconColor}`} />
            <span className={`text-sm font-medium ${config.textColor}`}>
              {config.title}
            </span>
          </div>
          {showReconnect && onReconnect && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onReconnect}
              className={config.textColor}
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Reconnect
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className={`p-4 rounded-xl border ${config.bgColor} ${config.borderColor}`}
      data-testid={`connection-alert-${status}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bgColor}`}>
          <Icon className={`w-5 h-5 ${config.iconColor}`} />
        </div>
        <div className="flex-1">
          <h4 className={`font-medium ${config.textColor}`}>
            {config.title}
            {connectionName && <span className="font-normal"> — {connectionName}</span>}
          </h4>
          <p className={`text-sm mt-1 ${config.textColor} opacity-80`}>
            {config.description}
          </p>
          
          {showReconnect && (
            <div className="flex items-center gap-3 mt-3">
              {onReconnect && (
                <Button
                  size="sm"
                  onClick={onReconnect}
                  className="bg-white dark:bg-slate-800 border shadow-sm"
                  variant="outline"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reconnect Bank
                </Button>
              )}
              <Link 
                to="/integrations"
                className={`text-sm ${config.textColor} hover:underline flex items-center gap-1`}
              >
                Manage connections
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to check connection health from API response
 */
export function useConnectionHealth(connections) {
  const unhealthyConnections = (connections || []).filter(conn => 
    conn.status === 'inactive' || 
    conn.status === 'expired' || 
    conn.status === 'error' ||
    conn.status === 'disconnected' ||
    conn.last_sync_error
  );
  
  return {
    hasIssues: unhealthyConnections.length > 0,
    unhealthyConnections,
    getConnectionStatus: (conn) => {
      if (conn.status === 'inactive') return 'inactive';
      if (conn.status === 'expired') return 'expired';
      if (conn.status === 'error') return 'error';
      if (conn.status === 'disconnected') return 'disconnected';
      if (conn.last_sync_error) return 'error';
      return 'active';
    }
  };
}
